import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';

import { customerService } from '../../../services/api';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import ListFilterToolbar from '../../../components/dashboard/ListFilterToolbar';
import RowActionsMenu from '../../../components/dashboard/RowActionsMenu';
import SuccessSnackbar from '../../../components/dashboard/SuccessSnackbar';
import UndoSnackbar from '../../../components/dashboard/UndoSnackbar';
import useUndoDelete from '../../../components/dashboard/useUndoDelete';
import DataGridCard from '../../../components/dashboard/DataGridCard';
import ErrorState from '../../../components/dashboard/ErrorState';
import PermissionDenied from '../../../components/dashboard/PermissionDenied';
import { retryOnServerError } from '../../../components/dashboard/queryOptions';
import { SendInviteButton, buildCollaboratorColumns, buildRowActions, showsSendInviteButton } from './columns';
import {
  AllStoresChip,
  CollaboratorStatusChip,
  STATUS_OPTIONS,
  accessSummary,
  inviteErrorMessage,
  roleSummary,
  useAccessOptions,
  useCollaboratorsCapabilities,
} from './collaboratorStatus';
import { ALL_STORES, EMPTY_FILTERS, matchesFilters } from './collaboratorFilters';

export const COLLABORATORS_QUERY_KEY = ['collaborators', 'admin'];

export default function CollaboratorsList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [selectionModel, setSelectionModel] = useState([]);
  const [showColumnBorder, setShowColumnBorder] = useState(false);
  const [showCellBorder, setShowCellBorder] = useState(false);
  const [flashMessage, setFlashMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [confirmSendAll, setConfirmSendAll] = useState(false);

  const collaboratorsQuery = useQuery({
    queryKey: COLLABORATORS_QUERY_KEY,
    retry: retryOnServerError,
    queryFn: () => customerService.listAllCollaborators().then((res) => res.data.results),
  });
  const { stores, roles } = useAccessOptions();
  const collectionCapabilities = useCollaboratorsCapabilities();

  const collaborators = useMemo(() => collaboratorsQuery.data ?? [], [collaboratorsQuery.data]);
  const filteredCollaborators = useMemo(
    () => collaborators.filter((collaborator) => matchesFilters(collaborator, filters)),
    [collaborators, filters],
  );

  // Every filter change keeps only the selected rows that are still visible.
  const updateFilter = useCallback(
    (field, value) => {
      const next = { ...filters, [field]: value };
      setFilters(next);
      const visibleIds = collaborators.filter((c) => matchesFilters(c, next)).map((c) => c.uuid);
      setSelectionModel((prev) => prev.filter((id) => visibleIds.includes(id)));
    },
    [filters, collaborators],
  );

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSelectionModel([]);
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['collaborators'] });

  const { pending, remove, undo, dismiss, seed } = useUndoDelete({
    onDelete: (uuid) => customerService.deleteCollaborator(uuid).then(invalidate),
    onRestore: (uuid) => customerService.restoreCollaborator(uuid).then(invalidate),
  });

  useEffect(() => {
    const seedDelete = location.state?.seedDelete;
    if (seedDelete) {
      seed(seedDelete.ids, seedDelete.message);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    if (location.state?.flashMessage) {
      setFlashMessage(location.state.flashMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const sendInvite = useMutation({
    mutationFn: (collaborator) => customerService.sendCollaboratorInvite(collaborator.uuid),
    onSuccess: (response) => {
      invalidate();
      setFlashMessage(t('collaborators.invite.sent', { email: response.data.email }));
    },
    onError: (error) => setErrorMessage(inviteErrorMessage(t, error)),
  });

  // Collaborators whose invitation was never sent or whose link expired, that
  // the user can (re)send, whatever the filters: "send all pending" means all.
  const pendingInvites = collaborators.filter(showsSendInviteButton);
  const expiredInvites = pendingInvites.filter((c) => c.status === 'invited').length;

  // No bulk endpoint: one send-invite per collaborator, and a partial failure
  // (e.g. an e-mail already active elsewhere) doesn't stop the others.
  const sendAllInvites = useMutation({
    mutationFn: (list) =>
      Promise.allSettled(list.map((collaborator) => customerService.sendCollaboratorInvite(collaborator.uuid))),
    onSuccess: (results) => {
      invalidate();
      const failed = results.filter((result) => result.status === 'rejected').length;
      const sent = results.length - failed;
      if (failed === 0) {
        setFlashMessage(t('collaborators.invite.sendAllSent', { count: sent }));
      } else {
        setErrorMessage(t('collaborators.invite.sendAllPartial', { sent, failed }));
      }
    },
    onSettled: () => setConfirmSendAll(false),
  });

  const restore = useMutation({
    mutationFn: (collaborator) => customerService.restoreCollaborator(collaborator.uuid),
    onSuccess: invalidate,
  });

  const handleDeleteOne = (collaborator) => {
    remove(collaborator.uuid, t('collaborators.deleteSuccess'));
    setSelectionModel((prev) => prev.filter((id) => id !== collaborator.uuid));
  };

  // Only rows the user can actually remove (the owner and removed ones can't).
  const deletableSelection = selectionModel.filter((id) =>
    collaborators.find((c) => c.uuid === id)?.authorization?.capabilities?.can_delete,
  );

  const handleDeleteSelected = () => {
    remove(deletableSelection, t('collaborators.deleteSuccess'));
    setSelectionModel([]);
  };

  const handlers = {
    onView: (collaborator) => navigate(`/collaborators/${collaborator.uuid}`),
    onEdit: (collaborator) => navigate(`/collaborators/${collaborator.uuid}/edit`),
    onSendInvite: (collaborator) => sendInvite.mutate(collaborator),
    onDelete: handleDeleteOne,
    onRestore: (collaborator) => restore.mutate(collaborator),
  };

  // Disables the row's "Enviar convite" while its request is in flight.
  const sendingInviteId = sendInvite.isPending ? sendInvite.variables?.uuid : null;

  const columns = useMemo(
    () => buildCollaboratorColumns({ t, language: i18n.language, sendingInviteId, ...handlers }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language, navigate, sendingInviteId],
  );

  const storeOptions = [
    { value: ALL_STORES, label: t('collaborators.scopes.all') },
    ...stores.map((store) => ({ value: store.uuid, label: store.name })),
  ];
  const filterLabels = {
    store: (value) => storeOptions.find((option) => option.value === value)?.label ?? value,
    status: (value) => t(`collaborators.statuses.${value}`),
    role: (value) => roles.find((role) => role.uuid === value)?.name ?? value,
  };
  const chips = Object.entries(filterLabels)
    .filter(([field]) => filters[field] !== '')
    .map(([field, label]) => ({
      key: field,
      label: `${t(`collaborators.filters.${field}`)}: ${label(filters[field])}`,
      onDelete: () => updateFilter(field, ''),
    }));

  const header = (
    <PageHeader
      title={t('collaborators.listTitle')}
      breadcrumbs={[
        { label: t('collaborators.breadcrumb.dashboard') },
        { label: t('nav.groups.team') },
        { label: t('collaborators.breadcrumb.list') },
      ]}
      actions={[
        pendingInvites.length > 0 && (
          <Button
            key="sendAll"
            variant="contained"
            color="secondary"
            startIcon={<SendOutlinedIcon />}
            onClick={() => setConfirmSendAll(true)}
          >
            {t('collaborators.invite.sendAll', { count: pendingInvites.length })}
          </Button>
        ),
        collectionCapabilities?.can_create && (
          <Button key="new" variant="contained" onClick={() => navigate('/collaborators/new')}>
            {t('collaborators.newCollaborator')}
          </Button>
        ),
      ].filter(Boolean)}
    />
  );

  // Plain props object, NOT a function/component: `slots.toolbar` must stay a
  // stable reference (see RolesList) or the search input loses focus.
  const toolbarProps = {
    filters: [
      {
        field: 'store',
        label: t('collaborators.filters.store'),
        value: filters.store,
        onChange: (value) => updateFilter('store', value),
        options: storeOptions,
      },
      {
        field: 'status',
        label: t('collaborators.filters.status'),
        value: filters.status,
        onChange: (value) => updateFilter('status', value),
        options: STATUS_OPTIONS.map((value) => ({ value, label: filterLabels.status(value) })),
      },
      {
        field: 'role',
        label: t('collaborators.filters.role'),
        value: filters.role,
        onChange: (value) => updateFilter('role', value),
        options: roles.map((role) => ({ value: role.uuid, label: role.name })),
      },
    ],
    search: {
      label: t('collaborators.filters.search'),
      value: filters.search,
      onChange: (value) => updateFilter('search', value),
    },
    chips,
    resultsLabel: t('collaborators.filters.resultsFound', { count: filteredCollaborators.length }),
    clearLabel: t('collaborators.filters.clear'),
    onClearAll: clearFilters,
    gridSettings: {
      showColumnBorder,
      onShowColumnBorderChange: setShowColumnBorder,
      showCellBorder,
      onShowCellBorderChange: setShowCellBorder,
    },
    bulkAction: deletableSelection.length > 0 && (
      <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={handleDeleteSelected}>
        {t('collaborators.list.deleteSelected', { count: deletableSelection.length })}
      </Button>
    ),
  };

  const renderBody = () => {
    if (collaboratorsQuery.isError) {
      if (collaboratorsQuery.error?.response?.status === 403) {
        return (
          <PermissionDenied
            title={t('collaborators.states.permissionDeniedTitle')}
            message={t('collaborators.states.permissionDeniedMessage')}
          />
        );
      }
      return (
        <ErrorState
          title={t('collaborators.states.errorTitle')}
          message={t('collaborators.states.errorMessage')}
          actionLabel={t('collaborators.states.retry')}
          onRetry={collaboratorsQuery.refetch}
        />
      );
    }

    return (
      <DataGridCard note={<FiltersNote t={t} />}>
        {isMobile ? (
          <Box>
            <ListFilterToolbar {...toolbarProps} showGridControls={false} />
            {filteredCollaborators.length === 0 ? (
              <EmptyState t={t} />
            ) : (
              <List disablePadding>
                {filteredCollaborators
                  .slice(
                    paginationModel.page * paginationModel.pageSize,
                    (paginationModel.page + 1) * paginationModel.pageSize,
                  )
                  .map((collaborator) => (
                    <ListItem
                      key={collaborator.uuid}
                      divider
                      secondaryAction={
                        <RowActionsMenu actions={buildRowActions({ t, collaborator, ...handlers })} />
                      }
                    >
                      <Checkbox
                        edge="start"
                        checked={selectionModel.includes(collaborator.uuid)}
                        onChange={(event) =>
                          setSelectionModel((prev) =>
                            event.target.checked
                              ? [...prev, collaborator.uuid]
                              : prev.filter((id) => id !== collaborator.uuid),
                          )
                        }
                        sx={{ mr: 1 }}
                      />
                      <ListItemText
                        primary={collaborator.name}
                        secondary={
                          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} useFlexGap flexWrap="wrap" component="span">
                            {collaborator.business_scope === 'all' ? (
                              <AllStoresChip t={t} />
                            ) : (
                              <Typography variant="caption" color="text.secondary" component="span">
                                {accessSummary(t, collaborator)}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" component="span">
                              {roleSummary(t, collaborator)}
                            </Typography>
                            <CollaboratorStatusChip t={t} collaborator={collaborator} />
                            {showsSendInviteButton(collaborator) && (
                              <SendInviteButton
                                t={t}
                                collaborator={collaborator}
                                onSendInvite={handlers.onSendInvite}
                                sending={sendingInviteId === collaborator.uuid}
                              />
                            )}
                          </Stack>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                  ))}
              </List>
            )}
          </Box>
        ) : (
          <DataGrid
            // Two-line name cell (name + email): let rows size to content.
            getRowHeight={() => 'auto'}
            rows={filteredCollaborators}
            columns={columns}
            getRowId={(row) => row.uuid}
            loading={collaboratorsQuery.isLoading}
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={(model) => setSelectionModel(model)}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            showColumnVerticalBorder={showColumnBorder}
            slots={{
              toolbar: ListFilterToolbar,
              noRowsOverlay: EmptyState,
              columnSelectorIcon: ViewColumnOutlinedIcon,
            }}
            slotProps={{
              toolbar: toolbarProps,
              noRowsOverlay: { t },
            }}
            localeText={{
              toolbarColumns: t('collaborators.filters.columns'),
              toolbarFilters: t('collaborators.filters.filters'),
            }}
            sx={{
              border: 0,
              ...(showCellBorder && {
                '& .MuiDataGrid-cell': { borderRight: '1px solid', borderColor: 'divider' },
              }),
            }}
          />
        )}
      </DataGridCard>
    );
  };

  return (
    <BoundedPageLayout header={header}>
      {renderBody()}

      <UndoSnackbar pending={pending} onUndo={undo} onDismiss={dismiss} undoLabel={t('collaborators.deleteUndo')} />

      <Dialog
        open={confirmSendAll}
        onClose={() => !sendAllInvites.isPending && setConfirmSendAll(false)}
      >
        <DialogTitle>{t('collaborators.invite.sendAllConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('collaborators.invite.sendAllConfirmMessage', { count: pendingInvites.length })}
            {expiredInvites > 0 && ` ${t('collaborators.invite.sendAllConfirmResends', { count: expiredInvites })}`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSendAll(false)} disabled={sendAllInvites.isPending}>
            {t('collaborators.actions.cancel')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={sendAllInvites.isPending}
            onClick={() => sendAllInvites.mutate(pendingInvites)}
          >
            {t('collaborators.invite.sendAllConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <SuccessSnackbar open={Boolean(flashMessage)} message={flashMessage} onClose={() => setFlashMessage('')} />

      <Snackbar
        open={Boolean(errorMessage)}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </BoundedPageLayout>
  );
}

// How the filters match, mainly the store one (all-stores access shows up in
// every store). Same footer note style as the metrics cards.
function FiltersNote({ t }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <HelpOutlineIcon color="secondary" fontSize="small" />
      <Typography variant="caption" color="text.secondary">
        {['store', 'role', 'status'].map((field) => (
          <React.Fragment key={field}>
            <Box component="span" sx={{ fontWeight: 'fontWeightBold' }}>
              {t(`collaborators.filters.${field}`)}:
            </Box>{' '}
            {t(`collaborators.filters.help.${field}`)}{' '}
          </React.Fragment>
        ))}
      </Typography>
    </Stack>
  );
}

function EmptyState({ t }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 6, textAlign: 'center' }}>
      <Typography variant="body1">{t('collaborators.list.empty')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('collaborators.list.emptyHint')}
      </Typography>
    </Stack>
  );
}
